import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMe, useUnreadCount } from '../hooks/useApi';
import { WorkspaceContext } from '../contexts/WorkspaceContext';
import { clearToken, clearWorkspaceId, getToken, getWorkspaceId } from '../api/client';
import posthog from '../lib/posthog';

function Avatar({ name, avatarUrl, size = 32 }: { name: string; avatarUrl?: string | null; size?: number }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        background: 'var(--rd-avatar-default)',
      }}
    >
      {initials}
    </div>
  );
}

function WorkspaceGlyph({ name, size = 40 }: { name: string; size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center font-semibold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.45,
        background: 'var(--rd-accent)',
        color: '#ffffff',
      }}
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

function SidebarIcon({
  to,
  icon,
  solidIcon,
  label,
  badge,
}: {
  to: string;
  icon: string;
  solidIcon: string;
  label: string;
  badge?: number;
}) {
  const location = useLocation();
  const isActive =
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      aria-label={label}
      title={label}
      className="relative flex items-center justify-center w-[44px] h-[44px] rounded-[var(--radius-md)] transition-colors duration-[120ms]"
      style={{
        color: isActive
          ? 'var(--color-icon-action-strong)'
          : 'var(--color-icon-neutral-medium)',
        background: isActive
          ? 'var(--color-surface-action-medium-default)'
          : 'transparent',
      }}
    >
      <i className={`icon ${isActive ? solidIcon : icon}`} />
      {badge != null && badge > 0 && (
        <span
          className="absolute top-[6px] right-[6px] w-[8px] h-[8px] rounded-full"
          style={{
            background: 'var(--color-surface-negative-medium-default)',
            border: '2px solid var(--color-surface-neutral-default)',
          }}
        />
      )}
    </NavLink>
  );
}

function NavItem({
  to,
  label,
  icon,
  solidIcon,
  badge,
  showDot,
}: {
  to: string;
  label: string;
  icon: string;
  solidIcon: string;
  badge?: number;
  showDot?: boolean;
}) {
  const location = useLocation();
  const isActive =
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      className="flex items-center gap-[8px] px-[8px] py-[6px] rounded-[6px] transition-colors duration-[120ms] no-underline select-none"
      style={{
        color: isActive ? 'var(--rd-text-strong)' : 'var(--rd-text-medium)',
        background: isActive ? 'rgba(0,0,0,0.05)' : 'transparent',
        fontWeight: isActive ? 600 : 500,
        fontSize: '14px',
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.05)';
          (e.currentTarget as HTMLElement).style.color = 'var(--rd-text-strong)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
          (e.currentTarget as HTMLElement).style.color = 'var(--rd-text-medium)';
        }
      }}
    >
      <i className={`icon ${isActive ? solidIcon : icon}`} style={{ fontSize: '16px', flexShrink: 0 }} />
      <span className="truncate flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span
          className="flex items-center justify-center rounded-full text-white ml-auto"
          style={{
            minWidth: '18px',
            height: '18px',
            fontSize: '11px',
            fontWeight: 600,
            padding: '0 5px',
            background: 'var(--color-surface-negative-medium-default)',
          }}
        >
          {badge}
        </span>
      )}
      {showDot && (badge == null || badge === 0) && (
        <span
          className="w-[6px] h-[6px] rounded-full ml-auto"
          style={{ background: 'var(--color-surface-negative-medium-default)' }}
        />
      )}
    </NavLink>
  );
}

function WorkspaceMenu({
  workspaceName,
  onOpenSettings,
}: {
  workspaceName: string;
  onOpenSettings: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const active = location.pathname.startsWith('/settings');

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((current) => !current)}
        className="cursor-pointer border-0 bg-transparent p-0 rounded-full"
        title={workspaceName}
        style={{
          boxShadow: active ? '0 0 0 3px var(--color-surface-action-medium-default)' : 'none',
          borderRadius: 999,
        }}
      >
        <WorkspaceGlyph name={workspaceName} />
      </button>

      {open && (
        <div
          className="absolute left-full top-0 ml-[var(--sp-3)] z-20 w-[220px] rounded-[var(--radius-md)] py-[var(--sp-1)] shadow-[var(--shadow-3)]"
          style={{
            background: 'var(--color-surface-neutral-default)',
            border: '1px solid var(--color-border-neutral-subtle-default)',
          }}
        >
          <div
            className="px-[var(--sp-4)] py-[var(--sp-3)]"
            style={{ borderBottom: '1px solid var(--color-border-neutral-subtle-default)' }}
          >
            <div
              className="text-[13px] font-semibold truncate"
              style={{ color: 'var(--color-text-neutral-strong)' }}
            >
              {workspaceName}
            </div>
            <div
              className="text-[13px]"
              style={{ color: 'var(--color-text-neutral-subtle)' }}
            >
              Workspace
            </div>
          </div>

          <button
            onClick={() => {
              setOpen(false);
              onOpenSettings();
            }}
            className="w-full flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-2)] text-left text-[13px] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
            style={{ color: 'var(--color-text-neutral-medium)' }}
          >
            <i className="icon icon-app-settings" />
            Workspace Settings
          </button>
        </div>
      )}
    </div>
  );
}

function UserMenu({
  userName,
  userEmail,
  avatarUrl,
  onOpenSettings,
  onLogout,
}: {
  userName: string;
  userEmail: string;
  avatarUrl?: string | null;
  onOpenSettings: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const active = location.pathname.startsWith('/me');

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((current) => !current)}
        className="cursor-pointer border-0 bg-transparent p-0 rounded-full"
        title="Personal settings"
        style={{
          boxShadow: active ? '0 0 0 3px var(--color-surface-action-medium-default)' : 'none',
          borderRadius: 999,
        }}
      >
        <Avatar name={userName} avatarUrl={avatarUrl} size={40} />
      </button>

      {open && (
        <div
          className="absolute left-full bottom-0 ml-[var(--sp-3)] z-20 w-[220px] rounded-[var(--radius-md)] py-[var(--sp-1)] shadow-[var(--shadow-3)]"
          style={{
            background: 'var(--color-surface-neutral-default)',
            border: '1px solid var(--color-border-neutral-subtle-default)',
          }}
        >
          <div
            className="px-[var(--sp-4)] py-[var(--sp-3)]"
            style={{ borderBottom: '1px solid var(--color-border-neutral-subtle-default)' }}
          >
            <div
              className="text-[13px] font-semibold truncate"
              style={{ color: 'var(--color-text-neutral-strong)' }}
            >
              {userName}
            </div>
            <div
              className="text-[13px] truncate"
              style={{ color: 'var(--color-text-neutral-subtle)' }}
            >
              {userEmail}
            </div>
          </div>

          <button
            onClick={() => {
              setOpen(false);
              onOpenSettings();
            }}
            className="w-full flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-2)] text-left text-[13px] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
            style={{ color: 'var(--color-text-neutral-medium)' }}
          >
            <i className="icon icon-app-settings" />
            Personal Settings
          </button>
          <button
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="w-full flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-2)] text-left text-[13px] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
            style={{ color: 'var(--color-text-neutral-medium)' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const isViewer = location.pathname.startsWith('/a/');
  const hasToken = !!getToken();
  const hasWorkspace = !!getWorkspaceId();
  const { data, loading: meLoading } = useMe();
  const { count: unreadCount } = useUnreadCount(hasToken && hasWorkspace);

  // All hooks must be before any early returns
  const handleLogout = useCallback(() => {
    clearToken();
    clearWorkspaceId();
    navigate('/login', { replace: true });
  }, [navigate]);

  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const wsMenuRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      const el = mainRef.current;
      if (el) {
        el.classList.add('page-transition');
        const onEnd = () => { el.classList.remove('page-transition'); el.removeEventListener('animationend', onEnd); };
        el.addEventListener('animationend', onEnd);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!wsMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (wsMenuRef.current && !wsMenuRef.current.contains(e.target as Node)) {
        setWsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [wsMenuOpen]);

  // Identify user in PostHog on every session load
  useEffect(() => {
    if (data?.user) {
      posthog.identify(data.user.id, {
        name: data.user.name,
        email: data.user.email,
      });
    }
  }, [data?.user?.id]);

  void WorkspaceMenu;
  void UserMenu;

  if (!hasToken) {
    navigate('/login', { replace: true });
    return null;
  }

  if (meLoading || !data) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div
          className="w-[32px] h-[32px] border-[3px] rounded-full animate-spin"
          style={{
            borderColor: 'var(--color-border-neutral-subtle-default)',
            borderTopColor: 'var(--color-surface-action-strong-default)',
          }}
        />
      </div>
    );
  }

  if (!data.workspace) {
    return (
      <div className="h-screen flex items-center justify-center px-[var(--sp-6)]">
        <div className="max-w-[420px] text-center">
          <p
            className="text-[20px] font-semibold m-0"
            style={{ color: 'var(--color-text-neutral-strong)' }}
          >
            No workspace available
          </p>
          <p
            className="text-[14px] mt-[var(--sp-3)]"
            style={{ color: 'var(--color-text-neutral-subtle)' }}
          >
            Your account signed in successfully, but no workspace was loaded for this session.
          </p>
          <button
            onClick={() => handleLogout()}
            className="mt-[var(--sp-4)] h-[40px] px-[var(--sp-4)] rounded-[var(--radius-md)] text-[14px] font-semibold cursor-pointer border-0 text-white"
            style={{ background: 'var(--color-surface-action-strong-default)' }}
          >
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  const canManageWorkspace = data.workspace.role === 'owner' || data.workspace.role === 'admin';

  return (
    <WorkspaceContext.Provider value={{ user: data.user, workspace: data.workspace }}>
      <div className="h-screen flex overflow-hidden" style={{ background: 'var(--rd-surface-page)' }}>
        {/* Desktop sidebar — always visible */}
        <nav
          role="navigation"
          aria-label="Main navigation"
          className="hidden md:flex flex-col overflow-y-auto"
          style={{ width: 240, flexShrink: 0, background: 'var(--rd-surface-page)', padding: '16px 12px' }}
        >
          {/* Workspace header with dropdown */}
          <div className="relative mb-[16px]" ref={wsMenuRef}>
            <button
              onClick={() => setWsMenuOpen((v) => !v)}
              className="w-full flex items-center gap-[8px] px-[8px] py-[4px] rounded-[6px] border-0 bg-transparent cursor-pointer transition-colors duration-[120ms]"
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div
                className="flex items-center justify-center font-bold flex-shrink-0"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: 'var(--rd-accent)',
                  color: '#ffffff',
                  fontSize: 11,
                }}
              >
                {data.workspace.name[0].toUpperCase()}
              </div>
              <span
                className="text-[14px] font-semibold truncate flex-1 text-left"
                style={{ color: 'var(--rd-text-strong)' }}
              >
                {data.workspace.name}
              </span>
              <span
                className="flex-shrink-0 flex items-center"
                style={{ color: 'var(--rd-text-meta)' }}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6l4 4 4-4" />
                </svg>
              </span>
            </button>

            {wsMenuOpen && (
              <div
                className="absolute left-[4px] right-[4px] top-full mt-[4px] z-50 rounded-[8px] border py-[4px]"
                style={{
                  background: 'var(--rd-surface-panel)',
                  borderColor: 'var(--rd-border)',
                  boxShadow: 'var(--rd-dropdown-shadow)',
                }}
              >
                {canManageWorkspace && (
                  <button
                    onClick={() => { setWsMenuOpen(false); navigate('/settings'); }}
                    className="w-full flex items-center gap-[8px] px-[12px] py-[8px] border-0 bg-transparent cursor-pointer text-left transition-colors duration-[120ms]"
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <i className="icon icon-app-settings" style={{ fontSize: 16, color: 'var(--rd-text-meta)' }} />
                    <span className="text-[13px]" style={{ color: 'var(--rd-text-medium)' }}>Workspace Settings</span>
                  </button>
                )}
                <button
                  onClick={() => { setWsMenuOpen(false); navigate('/me'); }}
                  className="w-full flex items-center gap-[8px] px-[12px] py-[8px] border-0 bg-transparent cursor-pointer text-left transition-colors duration-[120ms]"
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Avatar name={data.user.name} avatarUrl={data.user.avatar_url} size={18} />
                  <span className="text-[13px]" style={{ color: 'var(--rd-text-medium)' }}>Personal Settings</span>
                </button>
                <div style={{ height: 1, margin: '4px 8px', background: 'var(--rd-border-subtle)' }} />
                <button
                  onClick={() => { setWsMenuOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-[8px] px-[12px] py-[8px] border-0 bg-transparent cursor-pointer text-left transition-colors duration-[120ms]"
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--rd-text-meta)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" />
                    </svg>
                  <span className="text-[13px]" style={{ color: 'var(--rd-text-medium)' }}>Log out</span>
                </button>
              </div>
            )}
          </div>

          {/* Main nav items */}
          <div className="flex flex-col gap-[2px]">
            <NavItem
              to="/"
              label="Artifacts"
              icon="icon-app-dashboard"
              solidIcon="icon-app-dashboard-solid"
            />
            <NavItem
              to="/inbox"
              label="Inbox"
              icon="icon-communication-inbox-empty"
              solidIcon="icon-communication-inbox"
              badge={unreadCount}
              showDot={unreadCount != null && unreadCount > 0}
            />
          </div>

        </nav>

        {/* Content panel — fills from sidebar to right edge */}
        <div
          className="flex-1 flex flex-col overflow-hidden rounded-b-[12px] rounded-t-none"
          style={{
            background: 'var(--rd-surface-panel)',
            boxShadow: 'var(--rd-panel-shadow)',
            margin: '0 8px 8px 0',
          }}
        >
          <main ref={mainRef} role="main" className={`flex-1 min-w-0 ${isViewer ? 'overflow-hidden' : 'overflow-auto pb-[64px] md:pb-0'}`}>
            <Outlet />
          </main>
        </div>

        {!isViewer && (
          <nav
            role="navigation"
            aria-label="Mobile navigation"
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around h-[64px] border-t px-[var(--sp-2)]"
            style={{
              background: 'var(--color-surface-neutral-default)',
              borderColor: 'var(--color-border-neutral-subtle-default)',
            }}
          >
            <SidebarIcon
              to="/"
              icon="icon-app-dashboard"
              solidIcon="icon-app-dashboard-solid"
              label="Artifacts"
            />
            <SidebarIcon
              to="/inbox"
              icon="icon-communication-inbox-empty"
              solidIcon="icon-communication-inbox"
              label="Inbox"
              badge={unreadCount}
            />
            <button
              onClick={() => navigate('/settings')}
              className="border-0 bg-transparent p-0"
              title="Workspace Settings"
            >
              <WorkspaceGlyph name={data.workspace.name} size={36} />
            </button>
            <button
              onClick={() => navigate('/me')}
              className="border-0 bg-transparent p-0"
              title="Personal Settings"
            >
              <Avatar name={data.user.name} avatarUrl={data.user.avatar_url} size={36} />
            </button>
          </nav>
        )}
      </div>
    </WorkspaceContext.Provider>
  );
}

export { Avatar };
